import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateTaskModal from './components/CreateTaskModal';
import ReviewModal from './components/ReviewModal';
import {
  createTask, getTasksByManager, getTaskSubmissions, reviewSubmission
} from '../../api_service';

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: 'text-warning',  bg: 'bg-warning/10'  },
  SUBMITTED: { label: 'Submitted', color: 'text-primary',  bg: 'bg-primary/10'  },
  REVIEWED:  { label: 'Reviewed',  color: 'text-success',  bg: 'bg-success/10'  },
};

const TaskManagement = () => {
  const navigate = useNavigate();
  const managerId = sessionStorage.getItem('userId');

  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('tasks');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTasks(); }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await getTasksByManager(managerId);
      setTasks(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async (taskId) => {
    try {
      const res = await getTaskSubmissions(taskId);
      setSubmissions(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setSubmissions([]);
    }
  };

  const handleTaskClick = async (item) => {
    setSelectedTask(item);
    setActiveTab('submissions');
    await loadSubmissions(item.task.taskId);
  };

  const handleCreateTask = async (payload) => {
    await createTask(managerId, payload);
    await loadTasks();
  };

  const handleReview = async (submissionId, payload) => {
    await reviewSubmission(submissionId, { ...payload, reviewerId: managerId });
    if (selectedTask) await loadSubmissions(selectedTask.task.taskId);
    await loadTasks();
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning' };
    return { text: `${diff}d left`, cls: 'text-muted-foreground' };
  };

  const pendingReview = tasks.flatMap(t =>
    (t.submissions || []).filter(s => s.status === 'SUBMITTED')
  );

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="manager"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Task Management</h1>
            <p className="text-sm text-muted-foreground mt-1">Assign tasks, track submissions, and review trainee work</p>
          </div>
          <Button onClick={() => setShowCreate(true)} iconName="Plus" iconSize={16}>
            Create Task
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Tasks', value: tasks.length, icon: 'ClipboardList', color: 'text-primary' },
            { label: 'Total Assigned', value: tasks.reduce((s, t) => s + (t.totalAssigned || 0), 0), icon: 'Users', color: 'text-accent' },
            { label: 'Pending Review', value: tasks.reduce((s, t) => s + (t.submitted || 0), 0), icon: 'Clock', color: 'text-warning' },
            { label: 'Reviewed', value: tasks.reduce((s, t) => s + (t.reviewed || 0), 0), icon: 'CheckCircle', color: 'text-success' },
          ].map(card => (
            <div key={card.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name={card.icon} size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{card.value}</p>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Task list */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Tasks ({tasks.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : tasks.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="ClipboardList" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No tasks yet. Create your first task.</p>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto max-h-[500px]">
                {tasks.map(item => {
                  const dl = deadlineLabel(item.task?.deadline);
                  const isSelected = selectedTask?.task?.taskId === item.task?.taskId;
                  return (
                    <div
                      key={item.task?.taskId}
                      onClick={() => handleTaskClick(item)}
                      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-2">{item.task?.title}</p>
                        {dl && <span className={`text-xs whitespace-nowrap ${dl.cls}`}>{dl.text}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-muted-foreground">{item.totalAssigned} trainees</span>
                        {item.submitted > 0 && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{item.submitted} to review</span>
                        )}
                        {item.reviewed > 0 && (
                          <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">{item.reviewed} reviewed</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Submissions panel */}
          <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
            {!selectedTask ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                <Icon name="MousePointerClick" size={40} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select a task to view trainee submissions</p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">{selectedTask.task?.title}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedTask.task?.description || 'No description'}
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Deadline: {selectedTask.task?.deadline || 'None'}</span>
                    <span>Max score: {selectedTask.task?.maxScore}</span>
                  </div>
                </div>
                <div className="divide-y divide-border overflow-y-auto max-h-[460px]">
                  {submissions.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No submissions yet</div>
                  ) : (
                    submissions.map(sub => {
                      const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.PENDING;
                      return (
                        <div key={sub.submissionId} className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {sub.trainee?.firstname} {sub.trainee?.lastname}
                              </p>
                              <p className="text-xs text-muted-foreground">{sub.trainee?.emailid}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color}`}>
                                {cfg.label}
                              </span>
                              {sub.status === 'REVIEWED' && (
                                <span className="text-sm font-semibold text-foreground">
                                  {sub.score}/{selectedTask.task?.maxScore}
                                </span>
                              )}
                              {sub.status === 'SUBMITTED' && (
                                <Button size="sm" onClick={() => setReviewTarget(sub)}>
                                  Review
                                </Button>
                              )}
                              {sub.status === 'REVIEWED' && (
                                <Button size="sm" variant="outline" onClick={() => setReviewTarget(sub)}>
                                  Edit
                                </Button>
                              )}
                            </div>
                          </div>
                          {sub.submissionText && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 bg-muted/40 rounded p-2">
                              {sub.submissionText}
                            </p>
                          )}
                          {sub.mentorRemarks && (
                            <p className="text-xs text-primary mt-1">Remarks: {sub.mentorRemarks}</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {showCreate && (
        <CreateTaskModal
          managerId={managerId}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreateTask}
        />
      )}

      {reviewTarget && (
        <ReviewModal
          submission={reviewTarget}
          task={selectedTask?.task}
          onClose={() => setReviewTarget(null)}
          onReviewed={handleReview}
        />
      )}
    </div>
  );
};

export default TaskManagement;
